import { useState, useMemo } from 'react';
import {
  Users,
  Search,
  TrendingUp,
  X,
  ChevronDown,
  ChevronUp,
  Briefcase,
} from 'lucide-react';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { formatINR } from '@/shared/utils/currency';
import { cn } from '@/shared/utils/utils';
import type { PipelineRepStat, PipelineRepSummaryResponse } from '../types';

/** The hover on a win rate: what it is made of (2026-11-05). */
function winRateTitle(r: Pick<PipelineRepStat, 'wonDeals' | 'lostDeals' | 'lostIrrelevant' | 'winRate'>) {
  const irrelevant = r.lostIrrelevant || 0;
  const lost = Math.max(0, r.lostDeals - irrelevant);
  return `Win rate ${r.winRate}% = won ${r.wonDeals} ÷ (won ${r.wonDeals} + lost ${lost})`
    + (irrelevant ? `. ${irrelevant} lost as "Irrelevant" are left out — junk enquiries, not lost sales.` : '.');
}

/**
 * Who may see every employee's pipeline. Declared here, beside the component it
 * gates, because the rule was previously copy-pasted into `SalesPipelinePage`
 * and `PipelineRecordDetailPage` — and both copies carried `'ceo'` and
 * `'owner'`, values `userSchema.role` has never allowed. Those pages should
 * import this; the follow-up is noted in the dashboard audit.
 */
export const PIPELINE_MONITOR_ROLES = new Set(['superadmin', 'admin', 'department_admin']);

export const canMonitorPipeline = (role?: string | null) => !!role && PIPELINE_MONITOR_ROLES.has(role);

interface EmployeePipelineMonitorProps {
  summaryData?: PipelineRepSummaryResponse;
  isLoading: boolean;
  selectedOwnerId: string;
  onSelectOwner: (ownerId: string) => void;
  userRole?: string;
}

export function EmployeePipelineMonitor({
  summaryData,
  isLoading,
  selectedOwnerId,
  onSelectOwner,
  userRole,
}: EmployeePipelineMonitorProps) {
  const [repSearch, setRepSearch] = useState('');
  const [showTableMatrix, setShowTableMatrix] = useState(false);

  const reps = summaryData?.reps || [];
  const totals = summaryData?.totals;

  // Filter reps by search query
  const filteredReps = useMemo(() => {
    if (!repSearch.trim()) return reps;
    const term = repSearch.toLowerCase().trim();
    return reps.filter(
      (r) =>
        r.name.toLowerCase().includes(term) ||
        r.username.toLowerCase().includes(term) ||
        (r.department && r.department.toLowerCase().includes(term)) ||
        (r.role && r.role.toLowerCase().includes(term))
    );
  }, [reps, repSearch]);

  const selectedRep = useMemo(
    () => (selectedOwnerId ? reps.find((r) => r.id === selectedOwnerId) : null),
    [reps, selectedOwnerId]
  );

  const getRoleLabel = (role: string) => {
    if (role === 'superadmin') return 'Super Admin';
    if (role === 'admin') return 'Admin';
    if (role === 'department_admin') return 'Dept Admin';
    if (role === 'bde') return 'BDM';
    if (role === 'manager') return 'Manager';
    return role;
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  // `isLoading` was accepted as a prop and never read, so while the summary was
  // still in flight the monitor rendered an empty table — which a department
  // head reads as "nobody has a pipeline", not as "still loading". Placed after
  // every hook so the early return cannot break hook order.
  if (isLoading && reps.length === 0) {
    return (
      <div className="flex flex-col gap-3 rounded-2xl border border-primary/20 bg-surface p-4 shadow-sm">
        <div className="h-4 w-44 animate-pulse rounded bg-surface-variant" />
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-9 w-full animate-pulse rounded bg-surface-variant" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-primary/20 bg-surface p-4 shadow-sm">
      {/* Header Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Users className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-body-md font-semibold text-on-surface">
                Employee Sales Pipeline Monitor
              </h3>
              <Badge variant="soft" className="bg-primary/10 text-primary border-primary/20 text-data-xs font-bold">
                {userRole === 'superadmin' ? 'SUPER ADMIN' : userRole === 'department_admin' ? 'DEPT ADMIN' : 'ADMIN MONITOR'}
              </Badge>
            </div>
            <p className="text-body-xs text-on-surface-variant">
              Track, inspect, and compare individual sales pipelines across all reps & BDMs
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Quick Rep Search */}
          <div className="relative w-48 sm:w-56">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-on-surface-variant" />
            <input
              type="text"
              value={repSearch}
              onChange={(e) => setRepSearch(e.target.value)}
              placeholder="Search employee..."
              className="h-8 w-full rounded-lg border border-outline bg-surface-variant/40 pl-8 pr-2.5 text-body-xs text-on-surface placeholder:text-on-surface-variant/70 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {repSearch && (
              <button
                type="button"
                onClick={() => setRepSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          {/* Toggle Full Matrix View */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowTableMatrix(!showTableMatrix)}
            className="h-8 gap-1.5 text-body-xs font-medium"
          >
            {showTableMatrix ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {showTableMatrix ? 'Hide Rep Matrix' : 'Team Matrix'}
          </Button>
        </div>
      </div>

      {/* Reps Quick-Select Scroll Strip */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 pt-0.5 no-scrollbar">
        {/* All Team Pill */}
        <button
          type="button"
          onClick={() => onSelectOwner('')}
          className={cn(
            'flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-left transition border',
            !selectedOwnerId
              ? 'border-primary bg-primary/10 text-primary shadow-xs ring-1 ring-primary/30'
              : 'border-outline/70 bg-surface hover:border-outline hover:bg-surface-variant/30 text-on-surface'
          )}
        >
          <div className={cn(
            'flex h-7 w-7 items-center justify-center rounded-lg text-body-xs font-bold',
            !selectedOwnerId ? 'bg-primary text-on-primary' : 'bg-surface-variant text-on-surface-variant'
          )}>
            <Briefcase className="h-3.5 w-3.5" />
          </div>
          <div className="flex flex-col">
            <span className="text-body-xs font-semibold leading-tight">All Team</span>
            <span className="text-data-xs text-on-surface-variant">
              {totals?.openDeals ?? 0} open · {formatINR(totals?.openValue ?? 0)}
            </span>
          </div>
        </button>

        {/* Individual Employee Pills */}
        {filteredReps.map((rep) => {
          const isSelected = selectedOwnerId === rep.id;
          return (
            <button
              key={rep.id}
              type="button"
              onClick={() => onSelectOwner(rep.id)}
              className={cn(
                'flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-left transition border group',
                isSelected
                  ? 'border-primary bg-primary/10 text-primary shadow-xs ring-1 ring-primary/30'
                  : 'border-outline/70 bg-surface hover:border-outline hover:bg-surface-variant/30 text-on-surface'
              )}
            >
              <div
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded-lg text-body-xs font-bold transition',
                  isSelected
                    ? 'bg-primary text-on-primary'
                    : 'bg-primary/10 text-primary group-hover:bg-primary/20'
                )}
              >
                {getInitials(rep.name)}
              </div>
              <div className="flex flex-col min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-body-xs font-semibold truncate max-w-[120px] leading-tight">
                    {rep.name}
                  </span>
                  {rep.inactive && (
                    <span
                      className="text-[9px] font-bold px-1 py-0.5 rounded bg-slate-200 text-slate-700"
                      title={rep.leftBy?.length ? `Deals of people who left: ${rep.leftBy.join(', ')} — reassign them` : 'This person has left — reassign their open deals'}
                    >
                      {rep.leftBy?.length ? `${rep.leftBy.length} left` : 'inactive'}
                    </span>
                  )}
                  {rep.winRate > 0 && (
                    <span title={winRateTitle(rep)} className="text-[9px] font-bold px-1 py-0.5 rounded bg-emerald-100 text-emerald-800">
                      {rep.winRate}%
                    </span>
                  )}
                </div>
                <span className="text-data-xs text-on-surface-variant truncate max-w-[130px]">
                  {rep.openDeals} deals · {formatINR(rep.openValue)}
                </span>
              </div>
            </button>
          );
        })}

        {filteredReps.length === 0 && (
          <div className="px-3 py-2 text-body-xs text-on-surface-variant italic">
            No employee found matching "{repSearch}"
          </div>
        )}
      </div>

      {/* Selected Employee Focused Pipeline Profile Bar */}
      {selectedRep && (
        <div className="mt-1 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-primary/30 bg-primary/5 p-3.5 animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-on-primary font-bold text-body-sm shadow-sm">
              {getInitials(selectedRep.name)}
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-body-md font-bold text-on-surface">
                  {selectedRep.name}
                </span>
                <span className="text-body-xs text-on-surface-variant">
                  (@{selectedRep.username})
                </span>
                <Badge variant="soft" className="bg-primary/15 text-primary border-primary/25 text-data-xs">
                  {getRoleLabel(selectedRep.role)}
                </Badge>
                <Badge variant="soft" className="bg-surface-variant text-on-surface-variant text-data-xs">
                  {selectedRep.department}
                </Badge>
              </div>
              <p className="text-body-xs text-on-surface-variant">
                Currently monitoring this employee's individual sales pipeline records and stages
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-3 bg-surface/80 rounded-xl px-3 py-1.5 border border-outline/50 shadow-2xs">
              <div className="flex flex-col text-right">
                <span className="text-data-xs uppercase font-bold tracking-wider text-on-surface-variant">
                  Open Pipeline
                </span>
                <span className="text-body-sm font-bold text-primary">
                  {formatINR(selectedRep.openValue)}
                </span>
              </div>
              <div className="h-6 w-px bg-outline/50" />
              <div className="flex flex-col">
                <span className="text-data-xs uppercase font-bold tracking-wider text-on-surface-variant">
                  Open Deals
                </span>
                <span className="text-body-sm font-bold text-on-surface">
                  {selectedRep.openDeals}
                </span>
              </div>
              <div className="h-6 w-px bg-outline/50" />
              <div className="flex flex-col">
                <span className="text-data-xs uppercase font-bold tracking-wider text-emerald-700">
                  Won Deals
                </span>
                <span className="text-body-sm font-bold text-emerald-600">
                  {selectedRep.wonDeals} ({formatINR(selectedRep.wonValue)})
                </span>
              </div>
              <div className="h-6 w-px bg-outline/50" />
              <div className="flex flex-col">
                <span className="text-data-xs uppercase font-bold tracking-wider text-on-surface-variant">
                  Win Rate
                </span>
                <span className="text-body-sm font-bold text-indigo-600" title={winRateTitle(selectedRep)}>
                  {selectedRep.winRate}%
                </span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onSelectOwner('')}
              className="gap-1 h-9 rounded-xl border-primary/30 text-primary hover:bg-primary/10 font-semibold"
            >
              <X className="h-3.5 w-3.5" />
              Back to Team View
            </Button>
          </div>
        </div>
      )}

      {/* Expanded Team Matrix / Leaderboard Drawer */}
      {showTableMatrix && (
        <div className="mt-1 overflow-x-auto rounded-xl border border-outline bg-surface p-3 animate-in fade-in duration-200">
          <div className="flex items-center justify-between pb-2 mb-2 border-b border-outline/50">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              <span className="text-body-sm font-semibold text-on-surface">
                Sales Reps Pipeline Leaderboard & Comparison
              </span>
            </div>
            <span className="text-body-xs text-on-surface-variant">
              Click any rep to instantly inspect their full pipeline
            </span>
          </div>

          <table className="w-full text-left text-body-xs">
            <thead>
              <tr className="border-b border-outline/40 text-on-surface-variant uppercase text-data-xs tracking-wider">
                <th className="py-2 px-3 font-semibold">Employee</th>
                <th className="py-2 px-3 font-semibold">Role / Dept</th>
                <th className="py-2 px-3 font-semibold text-right">In Pipeline</th>
                <th className="py-2 px-3 font-semibold text-right">Pipeline Value</th>
                <th className="py-2 px-3 font-semibold text-right">Won Deals</th>
                <th className="py-2 px-3 font-semibold text-right">Won Value</th>
                <th className="py-2 px-3 font-semibold text-right">Lost</th>
                <th className="py-2 px-3 font-semibold text-center">Win Rate</th>
                <th className="py-2 px-3 font-semibold text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline/20">
              {reps.map((r) => {
                const isSelected = selectedOwnerId === r.id;
                return (
                  <tr
                    key={r.id}
                    onClick={() => onSelectOwner(r.id)}
                    className={cn(
                      'cursor-pointer transition hover:bg-surface-variant/30',
                      isSelected ? 'bg-primary/5 font-medium' : ''
                    )}
                  >
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-2">
                        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary text-data-xs font-bold">
                          {getInitials(r.name)}
                        </div>
                        <div>
                          <span className="font-semibold text-on-surface">{r.name}</span>
                          <span className="text-data-xs text-on-surface-variant ml-1.5">(@{r.username})</span>
                        </div>
                      </div>
                    </td>
                    <td className="py-2.5 px-3 text-on-surface-variant">
                      {getRoleLabel(r.role)} · {r.department}
                    </td>
                    <td className="py-2.5 px-3 text-right font-medium text-on-surface">
                      {r.openDeals}
                    </td>
                    <td className="py-2.5 px-3 text-right font-semibold text-primary">
                      {formatINR(r.openValue)}
                    </td>
                    <td className="py-2.5 px-3 text-right text-emerald-600 font-medium">
                      {r.wonDeals}
                    </td>
                    <td className="py-2.5 px-3 text-right text-emerald-700 font-medium">
                      {formatINR(r.wonValue)}
                    </td>
                    <td className="py-2.5 px-3 text-right text-rose-500 font-medium">
                      {r.lostDeals}
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <span title={winRateTitle(r)} className={cn(
                        'inline-flex items-center px-1.5 py-0.5 rounded text-data-xs font-bold',
                        r.winRate >= 50
                          ? 'bg-emerald-100 text-emerald-800'
                          : r.winRate > 0
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-surface-variant text-on-surface-variant'
                      )}>
                        {r.winRate}%
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <Button
                        type="button"
                        variant={isSelected ? 'primary' : 'outline'}
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectOwner(r.id);
                        }}
                        className="h-7 text-data-xs px-2.5 rounded-lg"
                      >
                        {isSelected ? 'Monitoring' : 'View Pipeline'}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
